// v1.3.0 — Template React `address_creation` (RX v1.3 §22.2.9)
// ═════════════════════════════════════════════════════════════════════════════
// Business Card BLOQUANTE — modal form. Déclenchée par le Prerequisite Engine
// quand une transaction géolocalisée nécessite une adresse.
// UX : modal centré, backdrop blur, form (label, rue, quartier, ville),
// bouton primary gold "Enregistrer l'adresse", bouton secondary GPS,
// bouton cancel discret.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useCallback, useMemo, useState } from 'react';
import './address_creation.css';

const CIV_COMMUNES = [
  'Cocody', 'Yopougon', 'Treichville', 'Plateau', 'Marcory',
  'Adjamé', 'Koumassi', 'Abobo', 'Attécoubé', 'Port-Bouët', 'Bingerville',
];

function EmptyState({ message = 'Adresse absente' }) {
  return (
    <div className="ac-empty" data-testid="rx-ac-empty">
      <div className="ac-empty-icon" aria-hidden="true">📍</div>
      <p>{message}</p>
    </div>
  );
}

function ValidatingOverlay() {
  return (
    <div className="ac-overlay-validating" data-testid="rx-ac-validating">
      <div className="ac-spinner" role="status" aria-live="polite">
        <div className="ac-spinner-dot" />
        <div className="ac-spinner-dot" />
        <div className="ac-spinner-dot" />
      </div>
      <p className="ac-validating-text">Validation de ton adresse…</p>
    </div>
  );
}

function SuccessBanner({ label }) {
  return (
    <div className="ac-success" data-testid="rx-ac-success" role="status">
      <div className="ac-success-check" aria-hidden="true">✓</div>
      <div>
        <p className="ac-success-title">Adresse enregistrée !</p>
        <p className="ac-success-sub">{label || 'Ton adresse par défaut est prête.'}</p>
      </div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="ac-error-banner" data-testid="rx-ac-error-banner" role="alert">
      <span className="ac-error-icon" aria-hidden="true">⚠</span>
      <span>{message || 'Certains champs sont invalides. Vérifie et réessaie.'}</span>
    </div>
  );
}

function AddressForm({
  section,
  status,
  onSubmit,
  onCancel,
  onRequestGps,
}) {
  const prefill = section?.suggested_prefill || {};
  const [label, setLabel] = useState(prefill.label || 'Domicile');
  const [landmark, setLandmark] = useState(prefill.street || '');
  const [district, setDistrict] = useState(prefill.district || '');
  const [touched, setTouched] = useState({});
  const [errorMsg, setErrorMsg] = useState(null);

  // 2026-07-02 (retour owner) — GPS-FIRST : une adresse AYA se GÉNÈRE par le
  // moteur d'adressage (GPS → rue la plus proche → code CIV précis, ex
  // « Numéro 240, Rue L155, Cocody »). Elle ne se tape pas. Le formulaire manuel
  // n'est qu'un secours qui produit une adresse APPROXIMATIVE (centre de commune),
  // clairement annoncée comme telle.
  const errors = useMemo(() => {
    const e = {};
    if (!label.trim()) e.label = 'Nom requis (ex : Domicile, Bureau)';
    if (!district.trim()) e.district = 'Choisis ta commune';
    return e;
  }, [label, district]);

  const canSubmit = Object.keys(errors).length === 0 && status !== 'validating';

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    setTouched({ label: true, district: true });
    if (!canSubmit) {
      setErrorMsg('Corrige les champs marqués avant de continuer.');
      return;
    }
    setErrorMsg(null);
    if (typeof onSubmit === 'function') {
      onSubmit({
        label: label.trim(),
        street: landmark.trim(), // repère facultatif (mémo humain, pas une rue officielle)
        district: district.trim(),
        city: 'Abidjan',
        is_default: true,
      });
    }
  }, [canSubmit, label, landmark, district, onSubmit]);

  return (
    <div className="ac-form" data-testid="rx-ac-form">
      {section?.reason ? (
        <p className="ac-reason" data-testid="rx-ac-reason">{section.reason}</p>
      ) : null}

      {errorMsg ? <ErrorBanner message={errorMsg} /> : null}

      {/* ── VOIE ROYALE : le moteur d'adressage génère l'adresse depuis le GPS ── */}
      {section?.gps_available !== false && (
        <button
          type="button"
          className="ac-btn ac-btn-primary"
          onClick={onRequestGps}
          disabled={status === 'validating'}
          data-testid="rx-ac-btn-gps"
        >
          📍 {section?.cta_secondary_label || 'Générer mon adresse avec ma position GPS'}
        </button>
      )}
      <p className="ac-gps-hint">
        Le moteur d’adressage AYA crée ton adresse précise :
        code <code>CIV-…</code> + rue la plus proche (ex&nbsp;: «&nbsp;Numéro 240, Rue L155, Cocody&nbsp;»).
      </p>

      {/* ── Secours sans GPS : adresse APPROXIMATIVE, annoncée comme telle ── */}
      <details className="ac-manual" data-testid="rx-ac-manual">
        <summary>Je ne peux pas activer le GPS</summary>
        <p className="ac-approx-warning" data-testid="rx-ac-approx-warning">
          ⚠ Sans GPS, ton adresse sera <strong>approximative</strong> (centre de ta commune).
          Tu pourras la préciser plus tard avec le GPS depuis Mon compte.
        </p>
        <form onSubmit={handleSubmit} noValidate>
          <label className="ac-field">
            <span className="ac-field-label">Nom de l’adresse</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, label: true }))}
              placeholder="Domicile, Bureau, Chez Maman…"
              maxLength={60}
              data-testid="rx-ac-input-label"
              className={touched.label && errors.label ? 'ac-input-error' : ''}
            />
            {touched.label && errors.label ? <em className="ac-field-error">{errors.label}</em> : null}
          </label>

          <label className="ac-field">
            <span className="ac-field-label">Commune</span>
            <select
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, district: true }))}
              data-testid="rx-ac-input-district"
              className={touched.district && errors.district ? 'ac-input-error' : ''}
            >
              <option value="">— Choisis ta commune —</option>
              {CIV_COMMUNES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {touched.district && errors.district ? <em className="ac-field-error">{errors.district}</em> : null}
          </label>

          <label className="ac-field">
            <span className="ac-field-label">Repère (optionnel)</span>
            <input
              type="text"
              value={landmark}
              onChange={(e) => setLandmark(e.target.value)}
              placeholder="Ex : face pharmacie, portail bleu…"
              maxLength={120}
              data-testid="rx-ac-input-street"
            />
          </label>

          <button
            type="submit"
            className="ac-btn ac-btn-secondary"
            disabled={!canSubmit}
            data-testid="rx-ac-btn-primary"
          >
            Créer une adresse approximative
          </button>
        </form>
      </details>

      <div className="ac-actions">
        <button
          type="button"
          className="ac-btn ac-btn-cancel"
          onClick={onCancel}
          disabled={status === 'validating'}
          data-testid="rx-ac-btn-cancel"
        >
          {section?.cta_cancel_label || 'Annuler'}
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Exports RX Engine
// ═════════════════════════════════════════════════════════════════════════════

export function AddressCreationTemplate({ payload, state, onSubmit, onCancel, onRequestGps }) {
  if (!payload || !Array.isArray(payload.sections) || payload.sections.length === 0) {
    return <EmptyState message="Aucun formulaire à afficher." />;
  }
  const section = payload.sections[0];
  // Preview-only override : permet à /rx/preview de forcer un état visuel via meta.preview_state
  const effectiveState = state || (payload.meta && payload.meta.preview_state) || 'initial';

  return (
    <div className="ac-modal-backdrop" data-testid="rx-ac-backdrop">
      <div className="ac-modal" role="dialog" aria-modal="true" data-testid="rx-ac-modal">
        <header className="ac-header" data-testid="rx-ac-header">
          <span className="ac-header-icon" aria-hidden="true">🏠</span>
          <h2 className="ac-title">{payload.header_text || 'Créons ton adresse pour continuer'}</h2>
        </header>

        {effectiveState === 'success' ? (
          <SuccessBanner label={section?.suggested_prefill?.label} />
        ) : (
          <AddressForm
            section={section}
            status={effectiveState}
            onSubmit={onSubmit}
            onCancel={onCancel}
            onRequestGps={onRequestGps}
          />
        )}

        {effectiveState === 'validating' ? <ValidatingOverlay /> : null}

        <footer className="ac-footer">
          Business Card <code>address_creation</code> — RX v1.3 §22.2.9 · Prerequisite Engine §27.5
        </footer>
      </div>
    </div>
  );
}

export function AddressCreationLoading() {
  return (
    <div className="ac-modal-backdrop" data-testid="rx-ac-loading">
      <div className="ac-modal ac-modal-loading">
        <div className="ac-skeleton ac-skeleton-header" />
        <div className="ac-skeleton ac-skeleton-line" />
        <div className="ac-skeleton ac-skeleton-line short" />
        <div className="ac-skeleton ac-skeleton-btn" />
      </div>
    </div>
  );
}

export function AddressCreationEmpty() {
  return <EmptyState message="Aucun formulaire d'adresse à afficher." />;
}

export function AddressCreationErrorState({ error }) {
  return (
    <div className="ac-modal-backdrop" data-testid="rx-ac-error-state">
      <div className="ac-modal ac-modal-error">
        <h3>⚠ Une erreur est survenue</h3>
        <p>{error?.message || 'Impossible d\'afficher le formulaire d\'adresse.'}</p>
      </div>
    </div>
  );
}

export default AddressCreationTemplate;

// ─── Metadata pour RX registry ────────────────────────────────────────────────
export const TEMPLATE_META = {
  template: 'address_creation',
  layout: 'form',
  priority: 'high',
  version: 'rx.v1',
  spec_ref: 'DESIGN_SYSTEM_V1.md §22.2.9',
  description: 'Business Card bloquante — form modal pour créer une adresse quand le Prerequisite Engine (§27.5) intercepte une transaction géolocalisée sans adresse par défaut.',
};
